import { Capacitor } from '@capacitor/core'
import { sortBy } from 'lodash'

import Api from 'services/Api'
import { t } from 'translations'
import { Format } from 'services/Dates'
import merinioSvgImage from 'assets/images/merinioLogo'
import { handleMobileSaveFiles } from 'services/Utilities'
import { getHolidays } from 'services/Holidays'
import { ContentTable, TDocumentDefinitions } from 'pdfmake/interfaces'

const exportPdfDaily = async (filter: { rangeStart: string }): Promise<void> => {
  let { result: events } = await Api.post('reports', { ...filter, type: 'dailyReport', date: Format(filter.rangeStart, { outFormat: 'date' }) })

  const dd: TDocumentDefinitions = {
    content: [],
    footer: {
      text: t('manager.schedule.reports.DAILY_REPORT_LEGEND'),
      alignment: 'center',
      style: ['small', 'quote']
    },
    styles: {
      generatedBy: { fontSize: 12, margin: [1, 2, 10, 0], color: 'grey', bold: true },
      small: { fontSize: 6 }
    },
    pageSize: 'A5',
    pageOrientation: 'landscape',
    // [left, top, right, bottom] or [horizontal, vertical]
    pageMargins: [10, 20, 10, 40]
  }

  // Add header
  if (Array.isArray(dd.content)) {
    const holidaysForTheDay = getHolidays(Format(filter.rangeStart))
    const dateTextToPrint = `${Format(filter.rangeStart, { outFormat: 'date' })} ${printHolidaysForPdfHeader(holidaysForTheDay)}`
    dd.content.push({
      columns: [
        { text: dateTextToPrint, alignment: 'left', style: 'header' },
        { text: [t('manager.schedule.reports.GENERATED_BY')], style: 'generatedBy', alignment: 'right' },
        { image: merinioSvgImage, width: 60 }
      ]
    })
  }

  const dailyReportPrint: ContentTable = {
    table: {
      widths: ['*', '*', '*', '*', '*', '*', '*'],
      body: [
        [
          { text: t('manager.schedule.reports.EMPLOYEE'), style: 'small', alignment: 'center' },
          { text: t('manager.schedule.reports.SHIFTS'), style: 'small', alignment: 'center' },
          { text: t('manager.schedule.reports.STATUS'), style: 'small', alignment: 'center' },
          { text: t('manager.schedule.reports.HOURS_MSG'), style: 'small', alignment: 'center' },
          { text: t('manager.schedule.reports.TAGS'), style: 'small', alignment: 'center' },
          { text: t('manager.schedule.reports.REGION'), style: 'small', alignment: 'center' },
          { text: t('manager.schedule.reports.COMMENT'), style: 'small', alignment: 'center' }
        ]
      ]
    },
    layout: {
      hLineWidth: (i, node) => (i === 0 || i === node.table.body.length ? 1 : 0.25),
      vLineWidth: (i, node) => (i === 0 || (node.table.widths && node.table.widths.length === i) ? 1 : 0.25),
      hLineColor: (i, node) => (i === 0 || i === node.table.body.length ? 'black' : 'gray'),
      vLineColor: (i, node) => (i === 0 || (node.table.widths && node.table.widths.length === i) ? 'black' : 'gray')
    }
  }

  events = sortBy(events, ['lastName', 'firstName'])

  for (const event of events) {
    // Formatting text display
    const tagFields = ['clientTags', 'locationTags', 'resourceTags']
    const employeeTags = tagFields.filter(field => !!event[field])
    const tagsTextData = employeeTags.map((field, i) => (!(i + 1 === employeeTags.length) ? `${event[field]},` : `${event[field]}`))
    const tagsText = tagsTextData.join(' ')

    let shiftInfoText = `${event.positionName} (${event.startTime} - ${event.endTime})`
    if (event.isExtraShift) shiftInfoText += ` - ${t('manager.schedule.reports.VARIABLE_END_TIME')}`
    if (event.isOvertime) shiftInfoText += ` - ${t('manager.schedule.reports.OVERTIME')}`
    if (event.isOnCall) shiftInfoText += ` - ${t('manager.schedule.reports.ISONCALL')}`
    if (event.isIrreplaceable) shiftInfoText += ` - ${t('manager.schedule.reports.IRREPLACEABLE')}`
    if (event.needsReplacement) shiftInfoText += ` - ${t('manager.schedule.reports.NEEDSREPLACEMENT')}`
    if (event.noEmployeeAvailable) shiftInfoText += ` - ${t('manager.schedule.reports.NO_EMPLOYEE_AVAILABLE')}`
    if (event.hasSpecificRequirements) shiftInfoText += ` - ${t('manager.schedule.reports.HAS_SPECIFIC_REQUIREMENTS')}`
    if (event.coveredOtherUnit) shiftInfoText += ` - ${t('manager.schedule.reports.COVEREDOTHERUNIT')}`

    const margin = [1, 1, 1, 1]

    const employeeInfo = {
      stack: [
        {
          text: event.employeeName !== '' ? `${event.lastName}, ${event.firstName} (${event.employeeNumber})` : t('manager.schedule.reports.UNASSIGNED'),
          style: 'small'
        }
      ],
      margin
    }
    const positionInfo = {
      stack: [{ text: shiftInfoText, style: 'small' }],
      margin
    }
    const status = {
      stack: [{ text: event.status, style: 'small' }],
      margin
    }
    const hoursInfo = {
      stack: [{ text: event.hoursWorkedThisPeriod + ' / ' + event.hoursPlannedThisPeriod, style: 'small' }],
      margin
    }
    const tags = {
      stack: [{ text: tagsText, style: 'small' }],
      margin
    }
    const region = {
      stack: [{ text: event.regionalTags, style: 'small' }],
      margin
    }
    const shiftComment = {
      stack: [{ text: event.shiftComment, style: 'small' }],
      margin
    }

    dailyReportPrint.table.body.push([employeeInfo, positionInfo, status, hoursInfo, tags, region, shiftComment])
  }

  if (Array.isArray(dd.content)) {
    dd.content.push(dailyReportPrint)
  }

  const fileName = `${t('manager.schedule.reports.PDF_DAILY_REPORT_TITLE', { date: Format(filter.rangeStart, { outFormat: 'date' }) })}.pdf`

  const pdfMake = (await import('pdfmake/build/pdfmake.min')).default
  const pdfFonts = (await import('pdfmake/build/vfs_fonts')).default
  pdfMake.vfs = pdfFonts.pdfMake.vfs
  // print the PDF
  if (!Capacitor.isNativePlatform()) pdfMake.createPdf(dd).download(fileName)
  else pdfMake.createPdf(dd).getBase64(b64 => handleMobileSaveFiles(b64, fileName, 'application/pdf'))
}

const printHolidaysForPdfHeader = holidays => {
  if (!holidays.length) return ''
  if (holidays.length === 1) return `(${holidays[0].name})`
  return `(${holidays.map(({ name }) => name).join(', ')})`
}

export default exportPdfDaily
